import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ReviewsSection from './components/ReviewsSection';
import SuccessMetrics from './components/SuccessMetrics';
import CertificationsSection from './components/CertificationsSection';
import TestimonialsSection from './components/TestimonialsSection';
import ComparisonSection from './components/ComparisonSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TrustCenter = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Trust Center - AdvoCall Professional | Vertrauen durch Transparenz</title>
        <meta
          name="description"
          content="Entdecken Sie unsere Erfolgsbilanz: 4,9/5 Sterne, 5.420 zufriedene Mandanten, 98,7% Erfolgsquote. Zertifizierungen, Bewertungen und Testimonials belegen unsere Kompetenz in der telefonischen Rechtsberatung." />
        <meta name="keywords" content="Anwalt Bewertungen, Rechtsberatung Erfahrungen, Anwaltskanzlei Testimonials, Rechtshilfe Vertrauen, AdvoCall Erfolgsquote" />
        <link rel="canonical" href="https://advocall.de/trust-center" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-primary to-accent text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-full mb-6">
              <Icon name="Shield" size={40} color="white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Trust Center
            </h1>
            <p className="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto mb-8">
              Vertrauen durch Transparenz - Entdecken Sie unsere Qualitätsmerkmale, 
              Zertifizierungen und echte Mandantenerfahrungen
            </p>
          </div>
        </div>
      </section>

      {/* Trust Indicators Bar */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Icon name="CheckCircle" size={32} className="text-trust-builder mb-2" />
              <span className="text-sm font-medium text-text-primary">DSGVO-konform</span>
              <span className="text-xs text-text-secondary">100% Datenschutz</span>
            </div>
            <div className="flex flex-col items-center">
              <Icon name="Scale" size={32} className="text-primary mb-2" />
              <span className="text-sm font-medium text-text-primary">Geprüfte Rechtsanwälte</span>
              <span className="text-xs text-text-secondary">Durch uns geprüfte Anwälte</span>
            </div>
            <div className="flex flex-col items-center">
              <Icon name="Clock" size={32} className="text-conversion-accent mb-2" />
              <span className="text-sm font-medium text-text-primary">24/7 verfügbar</span>
              <span className="text-xs text-text-secondary">Rund um die Uhr</span>
            </div>
            <div className="flex flex-col items-center">
              <Icon name="Award" size={32} className="text-accent mb-2" />
              <span className="text-sm font-medium text-text-primary">Ausgezeichnet</span>
              <span className="text-xs text-text-secondary">Top Bewertet auf Google und bei anwalt.de</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <SuccessMetrics />
      <ReviewsSection />
      <TestimonialsSection />
      <CertificationsSection />
      <ComparisonSection />

      {/* Call to Action Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-accent text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Überzeugt von unserer Kompetenz?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Erhalten Sie sofortige professionelle Rechtsberatung von zugelassenen Anwälten.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button
              variant="default"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              className="bg-white text-primary hover:bg-slate-50 cta-button">
              Jetzt anrufen - 2,99 €/Min
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-primary">
              Beratung planen
            </Button>
          </div>
          <p className="text-sm opacity-80 mt-4">
            Transparente Preise • Keine versteckten Kosten • Sofortige Hilfe
          </p>
        </div>
      </section>

      {/* Footer Trust Statement */}
      <section className="py-12 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-3">
              <Icon name="Scale" size={24} className="text-primary" />
              <span className="text-xl font-bold text-primary">AdvoCall</span>
            </div>
          </div>
          <p className="text-text-secondary leading-relaxed">
            Als ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH stehen wir für höchste professionelle Standards,
            vollständige Transparenz und vertrauensvolle Mandantenbeziehungen.
          </p>
          <div className="flex items-center justify-center space-x-6 mt-6 text-sm text-text-secondary">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={16} />
              <span>Frankfurt am Main</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Phone" size={16} />
              <span>24/7 erreichbar</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Mail" size={16} />
              <span>info@advocall.de</span>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default TrustCenter;